import {
  animate,
  group,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  model,
} from '@angular/core';
import { SpinnerComponent } from '../spinner/spinner.component';

let nextToggleCardId = 0;

const INTERACTIVE_TARGET_SELECTOR = [
  'a',
  'button',
  'input',
  'select',
  'textarea',
  'label',
  'ng-select',
  '.ng-select',
  '.ng-dropdown-panel',
  '[role="combobox"]',
  '[role="listbox"]',
  '[role="option"]',
  '[role="button"]',
  '[contenteditable="true"]',
  '[data-toggle-card-ignore]',
].join(', ');

@Component({
  selector: 'ct-lib-toggle-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './toggle-card.component.html',
  styleUrls: ['./toggle-card.component.scss'],
  imports: [SpinnerComponent],
  host: {
    class: 'ct-card',
    '[class.interactive]': '!disabled() && !busy()',
    '[class.checked]': 'checked()',
    '[class.disabled]': 'disabled() || busy()',
    '(click)': 'onCardClick($event)',
  },
  animations: [
    trigger('mirror', [
      state(
        'open',
        style({
          height: '*',
          overflow: 'inherit',
          opacity: 1,
        }),
      ),
      state(
        'closed, void',
        style({
          height: 0,
          overflow: 'hidden',
          opacity: 0,
        }),
      ),
      transition(
        'open => closed',
        group([animate('0.3s ease'), style({ overflow: 'hidden' })]),
      ),
      transition('closed => open', group([animate('0.3s ease')])),
    ]),
  ],
})
export class ToggleCardComponent {
  readonly title = input.required<string>();
  readonly description = input<string | null>(null);
  readonly checked = model(false);
  readonly disabled = input(false);
  readonly busy = input(false);
  readonly switchAriaLabel = input<string | null>(null);

  readonly switchId = `ct-lib-toggle-card-${++nextToggleCardId}`;

  /**
   * Toggles the card when clicking non-interactive areas.
   *
   * @param {MouseEvent} event Host click event.
   * @returns {void}
   */
  onCardClick(event: MouseEvent): void {
    if (
      this.disabled() ||
      this.busy() ||
      this.isInteractiveTarget(event.target)
    ) {
      return;
    }
    this.checked.set(!this.checked());
  }

  /**
   * Applies a switch change from the checkbox control.
   *
   * @param {boolean} checked New checked state.
   * @returns {void}
   */
  onToggle(checked: boolean): void {
    if (this.disabled() || this.busy()) {
      return;
    }
    this.checked.set(checked);
  }

  /**
   * Whether the click target is an interactive child that should not toggle the card.
   *
   * @param {EventTarget | null} target Click target.
   * @returns {boolean} True when the card click should be ignored.
   */
  private isInteractiveTarget(target: EventTarget | null): boolean {
    return (
      target instanceof Element && !!target.closest(INTERACTIVE_TARGET_SELECTOR)
    );
  }
}
